import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import gameData from '../gameData';
import DraggableSprite from '../skeletal/DraggableSprite';
import SpriteLabel from '../skeletal/SpriteLabel';
import SoundAssetKeys from '../SoundAssetKeys';
import UIAssetKeys from '../UIAssetKeys';
import AssemblyGameScreen from './AssemblyGameScreen';
import LocalizationSoundAssetKeys from '../Localization-SoundAssetKeys';
import DraggableBoneName from '../skeletal/DraggableBoneName';
import RoundOverPopup from '../skeletal/RoundOverPopup';

export default class LabelGameScreen extends Phaser.Scene {
  static get KEY() { return 'LabelGameScreen'; }

  constructor() {
    super({ key: LabelGameScreen.KEY });
  }

  init() {
    this.attempts = 0;
    this.adjustY = 100;

    this.boneMaskNames = ['left_hand_m',
      'right_hand_m',
      'left_fibula_m',
      'left_foot_m',
      'left_humerus_m',
      'left_radius_m',
      'left_femur_m',
      'left_tibia_m',
      'left_ulna_m',
      'pelvis_m',
      'right_fibula_m',
      'right_foot_m',
      'right_humerus_m',
      'right_radius_m',
      'right_femur_m',
      'right_tibia_m',
      'right_ulna_m',
      'scapula_m',
      'skull_m',
      'spine_m',
      'clavicle_m',
      'ribs_m',
      'sternum_m',
      'face_m',
      'right_patella_m',
      'left_patella_m'];

    this.boneNames = ['left_hand',
      'right_hand',
      'left_fibula',
      'left_foot',
      'left_humerus',
      'left_radius',
      'left_femur',
      'left_tibia',
      'left_ulna',
      'pelvis',
      'right_fibula',
      'right_foot',
      'right_humerus',
      'right_radius',
      'right_femur',
      'right_tibia',
      'right_ulna',
      'scapula',
      'skull',
      'spine',
      'clavicle',
      'ribs',
      'sternum',
      'face',
      'right_patella',
      'left_patella'];

    this.boneLabels = ['skull',
      'sternum',
      'ribs',
      'spine',
      'pelvis',
      'femur',
      'patella',
      'fibula',
      'metacarpals',
      'metatarsals',
      'face',
      'clavicle',
      'scapula',
      'humerus',
      'radius',
      'ulna',
      'hand',
      'tibia',
      'carpals',
      'tarsals',
      'foot'];

    this.wordsPlacedCorrectly = 0;

    this.boneSprites = [];

    /** @type {Phaser.GameObjects.Image[]} */
    this.boneTargets = [];

    /** @type {{x:number, y:number}[]} */
    this.boneLabelPositions = [];

    /** @type {{x:number, y:number}[]} */
    this.boneWordPositions = [];

    // drag fix
    this.input.on(Phaser.Input.Events.GAME_OUT, (time, event) => {
      if (event instanceof MouseEvent) {
        this.input.manager.onMouseUp(event);
      }
    });
  }

  create() {
    const width = this.scale.width;
    const height = this.scale.height;

    // make bg
    const bgShape = this.add.rectangle(0, 0, width, height, 0x1793C7).setOrigin(0);

    for (let i = 0; i < this.boneNames.length; i++) {
      const name = this.boneNames[i];

      const bmap = ABCyaPhaser.Images.addImage(0, 0, name, this).setScale(0.7);
      bmap.name = name;
      this.boneTargets.push(bmap);
    }

    this.eyesRed = ABCyaPhaser.Images.addImage(0, 0, UIAssetKeys._eyes_red, this).setAlpha(0)
      .setScale(0.7);

    this.eyesGreen = ABCyaPhaser.Images.addImage(0, 0, UIAssetKeys._eyes_green, this).setAlpha(0)
      .setScale(0.7);

    this.positionBones();

    this.makeLabelPositions();

    // make the bone labels that point to bones
    for (let i = 0; i < this.boneLabels.length; i++) {
      const spriteName = this.boneLabels[i];
      this.placeBoneLabel(spriteName, i);
    }

    this.makeWordPositions();

    // make the bone words that will be dragged to labels or bones
    for (let i = 0; i < this.boneLabels.length; i++) {
      const spriteName = this.boneLabels[i];
      this.placeBoneWord(spriteName, i);
    }

    this.soundID = ABCyaPhaser.Audio.createSound(this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);
    ABCyaPhaser.Audio.playSound(LocalizationSoundAssetKeys._label_the_skeleton, this.soundID);

    this.makeClicksLabel();
  }

  positionBones() {
    for (let i = 0; i < this.boneTargets.length; i++) {
      const sprite = this.boneTargets[i];

      switch (sprite.name) {
        case 'clavicle':
          sprite.x = 513;
          sprite.y = 106;
          break;

        case 'skull':
          sprite.x = 512;
          sprite.y = 40;
          break;

        case 'face':
          sprite.x = 512;
          sprite.y = 60;
          this.eyesRed.x = 512;
          this.eyesRed.y = 160;
          this.eyesGreen.x = 512;
          this.eyesGreen.y = 160;
          break;

        case 'pelvis':
          sprite.x = 513;
          sprite.y = 263;
          break;

        case 'spine':
          sprite.x = 513;
          sprite.y = 173;
          break;

        case 'right_femur':
          sprite.x = 481;
          sprite.y = 340;
          break;

        case 'left_femur':
          sprite.x = 546;
          sprite.y = 338;
          break;

        case 'left_foot':
          sprite.x = 544;
          sprite.y = 544;
          break;

        case 'right_foot':
          sprite.x = 480;
          sprite.y = 544;
          break;

        case 'left_tibia':
          sprite.x = 537;
          sprite.y = 470;
          break;

        case 'right_tibia':
          sprite.x = 488;
          sprite.y = 470;
          break;

        case 'left_fibula':
          sprite.x = 544;
          sprite.y = 468;
          break;

        case 'right_fibula':
          sprite.x = 481;
          sprite.y = 469;
          break;

        case 'left_patella':
          sprite.x = 541;
          sprite.y = 410;
          break;

        case 'right_patella':
          sprite.x = 487;
          sprite.y = 411;
          break;

        case 'scapula':
          sprite.x = 512;
          sprite.y = 131;
          break;

        case 'left_humerus':
          sprite.x = 575;
          sprite.y = 159;
          break;

        case 'right_humerus':
          sprite.x = 452;
          sprite.y = 159;
          break;

        case 'right_radius':
          sprite.x = 432;
          sprite.y = 240;
          break;

        case 'left_radius':
          sprite.x = 594;
          sprite.y = 241;
          break;

        case 'ribs':
          sprite.x = 513;
          sprite.y = 145;
          break;

        case 'left_hand':
          sprite.x = 611;
          sprite.y = 304;
          break;

        case 'right_hand':
          sprite.x = 413;
          sprite.y = 302;
          break;

        case 'left_ulna':
          sprite.x = 583;
          sprite.y = 242;
          break;

        case 'right_ulna':
          sprite.x = 443;
          sprite.y = 242;
          break;

        case 'sternum':
          sprite.x = 513;
          sprite.y = 123;
          break;
      }

      sprite.y += this.adjustY;
    }
  }

  placeBoneLabel(spriteName, i) {
    const fullSpriteName = `box_${spriteName}`;

    const sprite = ABCyaPhaser.Images.addImage(0, 0, fullSpriteName, this);
    sprite.name = fullSpriteName;
    sprite.enabled = true;
    this.boneTargets.push(sprite);

    sprite.scale = 0.70;

    const posArray = this.boneLabelPositions[i];

    sprite.x = posArray.x;
    sprite.y = posArray.y;
  }

  // make 17 positions for bones and randomize them
  // 9 on one side, 8 on the other
  makeWordPositions() {
    for (let i = 0; i < this.boneLabels.length; i++) {
      let x = 0;
      let y = 0;

      if (i < 11) {
        x = 170;
      }
      else {
        x = 860;
      }

      y = ((i % 11) * 45) + 30 + this.adjustY;
      this.boneWordPositions.push({ x, y });
    }
    this.boneWordPositions = Phaser.Utils.Array.Shuffle(this.boneWordPositions);
  }

  placeBoneWord(spriteName, i) {
    const targets = this.getAllTargetsByName(spriteName);

    const fullSpriteName = `word_${spriteName}`;

    const sprite = new DraggableBoneName(this, ABCyaPhaser.Localization.instance.getLocalizedString(`labels.${spriteName}`), spriteName);
    sprite.game = 'label';
    sprite.enabled = true;

    sprite.i = i;

    sprite.scale = 0.70;
    sprite.hitTargets = targets;

    sprite.hitListener = this.targetHit.bind(this);
    sprite.outListener = this.targetOut.bind(this);
    sprite.doClickAction = this.dragMouseDown.bind(this);

    this.add.existing(sprite);

    const posArray = this.boneWordPositions[i];

    sprite.x = posArray.x;
    sprite.y = posArray.y;

    sprite.lastPosX = sprite.x;
    sprite.lastPosY = sprite.y;
  }

  getAllTargetsByName(spriteName) {
    /** @type {Phaser.GameObjects.Image[]} */
    const matchesArray = [];

    for (let i = 0; i < this.boneTargets.length; i++) {
      const target = this.boneTargets[i];
      if (target.name.includes(spriteName)) {
        matchesArray.push(target);
      }
    }

    return matchesArray;
  }

  /**
   * 
   * @param {DraggableSprite} sprite 
   */
  targetHit(sprite) {
    this.flashCorrect();

    this.attempts++;

    this.clicksLabel.setValue(this.attempts);

    const target = sprite.hitTargets[sprite.hitTargets.length - 1];
    let xPos = target.x + (target.displayWidth / 2) - (sprite._bg.width * sprite.scaleX / 2);
    if (sprite.i < 10) {
      xPos = target.x - (target.displayWidth / 2) + (sprite._bg.width * sprite.scaleX / 2);
    }
    sprite.setPosition(xPos, target.y);

    // eslint-disable-next-line no-param-reassign
    sprite.enabled = false;

    this.wordsPlacedCorrectly++;

    if (this.wordsPlacedCorrectly === this.boneLabels.length) {
      this.gameWon();
    }
  }

  flashCorrect() {
    this.tweens.killTweensOf(this.eyesRed);
    this.tweens.killTweensOf(this.eyesGreen);

    this.eyesRed.alpha = 0;
    this.eyesGreen.alpha = 0;

    ABCyaPhaser.Tweens.addTween(this.eyesGreen, 250, { alpha: 1 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesGreen, 30, { alpha: 0, delay: 250 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesGreen, 250, { alpha: 1, delay: 280 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesGreen, 30, { alpha: 0, delay: 530 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesGreen, 250, { alpha: 1, delay: 560 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesGreen, 30, { alpha: 0, delay: 810 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesGreen, 250, { alpha: 1, delay: 840 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesGreen, 30, { alpha: 0, delay: 1090 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesGreen, 250, { alpha: 1, delay: 1120 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesGreen, 30, { alpha: 0, delay: 1370 }, Phaser.Math.Easing.Bounce.In);
  }

  gameWon() {
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._cartoon_win, this);

    // best score
    let bestScore = this.attempts;
    if (bestScore > gameData.LABEL_BEST_SCORE) {
      bestScore = gameData.LABEL_BEST_SCORE;
    }
    else {
      gameData.LABEL_BEST_SCORE = bestScore;
    }

    this.wonDialog = new RoundOverPopup(this, 1, this.attempts, bestScore);

    this.wonDialog.x = this.scale.width / 2;
    this.wonDialog.y = this.scale.height / 2;

    this.add.existing(this.wonDialog);

    this.wonDialog.doAction1 = this.playAgain.bind(this);
    this.wonDialog.doAction2 = this.playOtherGame.bind(this);

    ABCyaPhaser.Tweens.addTween(this.wonDialog, 900, { scale: 1.2 }, Phaser.Math.Easing.Bounce.Out);
  }

  playAgain() {
    this.playLabelGame();
  }

  playLabelGame() {
    // restart scene
    this.scene.start();
  }

  playOtherGame() {
    this.playAssembleGame();
  }

  playAssembleGame() {
    this.scene.start(AssemblyGameScreen.KEY);
  }

  /**
   * 
   * @param {DraggableSprite} sprite 
   */
  targetOut(sprite) {
    this.flashIncorrect();

    this.attempts++;

    this.clicksLabel.setValue(this.attempts);

    sprite.moveToLastPosition();

  }

  flashIncorrect() {
    this.tweens.killTweensOf(this.eyesRed);
    this.tweens.killTweensOf(this.eyesGreen);

    this.eyesRed.alpha = 0;
    this.eyesGreen.alpha = 0;

    ABCyaPhaser.Tweens.addTween(this.eyesRed, 250, { alpha: 1 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesRed, 30, { alpha: 0, delay: 250 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesRed, 250, { alpha: 1, delay: 280 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesRed, 30, { alpha: 0, delay: 530 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesRed, 250, { alpha: 1, delay: 560 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesRed, 30, { alpha: 0, delay: 810 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesRed, 250, { alpha: 1, delay: 840 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesRed, 30, { alpha: 0, delay: 1090 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesRed, 250, { alpha: 1, delay: 1120 }, Phaser.Math.Easing.Bounce.In);
    ABCyaPhaser.Tweens.addTween(this.eyesRed, 30, { alpha: 0, delay: 1370 }, Phaser.Math.Easing.Bounce.In);
  }

  dragMouseDown() {
    if (this.soundID) {
      this.soundID.destroy();
      this.soundID = null;
    }
  }

  makeClicksLabel() {
    this.clicksLabel = new SpriteLabel(this, 1, UIAssetKeys._clicks_dialog);
    this.clicksLabel.x = this.scale.width - 105;
    this.clicksLabel.y = this.scale.height - 50;

    this.add.existing(this.clicksLabel);
  }

  makeLabelPositions() {
    for (let i = 0; i < this.boneLabels.length; i++) {
      const name = this.boneLabels[i];

      let x = 0;
      let y = 0;

      if (i < 11) {
        x = 100;
        y = 300;
      }
      else {
        x = 700;
        y = 300;
      }

      switch (name) {
        case 'skull': // skull
          x = 377;
          y = 29;
          break;

        case 'sternum': // sternum
          x = 386;
          y = 126;
          break;

        case 'ribs': // ribs
          x = 367;
          y = 166;
          break;

        case 'spine': // spine
          x = 386;
          y = 205;
          break;

        case 'pelvis': // pelvis
          x = 373;
          y = 251;
          break;

        case 'femur': // femur
          x = 368;
          y = 325;
          break;

        case 'patella': // patella
          x = 375;
          y = 411;
          break;

        case 'fibula': // fibula
          x = 371;
          y = 468;
          break;

        case 'face': // face
          x = 648;
          y = 60;
          break;

        case 'clavicle': // clavicle
          x = 668;
          y = 104;
          break;

        case 'scapula': // scapula
          x = 660;
          y = 138;
          break;

        case 'humerus': // humerus
          x = 673;
          y = 176;
          break;

        case 'radius': // radius
          x = 678;
          y = 216;
          break;

        case 'ulna': // ulna
          x = 680;
          y = 256;
          break;

        case 'hand': // hand
          x = 693;
          y = 318;
          break;

        case 'tibia':// tibia
          x = 655;
          y = 456;
          break;

        // new
        case 'carpals':
          x = 688;
          y = 287;
          break;

        case 'tarsals':
          x = 660;
          y = 535;
          break;

        case 'foot': // foot
          x = 665;
          y = 562;
          break;

        case 'metacarpals':
          x = 337;
          y = 295;
          break;

        case 'metatarsals':
          x = 371;
          y = 550;
          break;
      }

      y += this.adjustY;

      this.boneLabelPositions.push({ x, y });
    }
  }
}